import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import StudyDialog from "@/components/StudyDialog";

/**
 * Rotating spotlight for the flagship clinical actives — shown above the full
 * formula table so shoppers meet the science before scanning the whole panel.
 * Auto-advances every 6s; pauses on hover; manual dots + arrows.
 */
export default function HeroActiveSpotlight({ ingredients = [], sampleTheme = false, mobileSafe = false }) {
  const heroes = ingredients.filter((i) => i.flagship && i.study);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback((n) => setIdx((prev) => (n + heroes.length) % heroes.length), [heroes.length]);

  useEffect(() => {
    if (paused || heroes.length <= 1) return;
    const t = setInterval(() => setIdx((p) => (p + 1) % heroes.length), 6000);
    return () => clearInterval(t);
  }, [paused, heroes.length]);

  if (!heroes.length) return null;
  const active = heroes[idx];

  return (
    <div
      className="border border-[color:var(--line)] bg-[#0f0f10] text-[#faf9f5] overflow-hidden"
      data-testid="hero-active-spotlight"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="grid grid-cols-1 lg:grid-cols-12">
        {/* Left: rotating content with editorial macro image */}
        <div className={`lg:col-span-8 relative md:p-12 min-h-[320px] flex flex-col overflow-hidden ${sampleTheme || mobileSafe ? "p-6" : "p-8"}`}>
          <AnimatePresence mode="wait">
            <motion.div
              key={active.slug + "-bg"}
              initial={{ opacity: 0, scale: 1.06 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0"
            >
              {active.image && <img src={active.image} alt="" className="w-full h-full object-cover" data-testid="spotlight-active-image" />}
              <div className="absolute inset-0 bg-gradient-to-r from-[#0f0f10] via-[#0f0f10]/92 to-[#0f0f10]/45" />
            </motion.div>
          </AnimatePresence>

          <div className="relative z-10 flex flex-col flex-1">
          <div className={`flex items-center justify-between ${sampleTheme || mobileSafe ? "flex-wrap gap-3" : ""}`}>
            <div className="label !text-[10px] !text-[#faf9f5]/55" data-testid="spotlight-position">Flagship Active · {String(idx + 1).padStart(2, "0")} / {String(heroes.length).padStart(2, "0")}</div>
            <div className="label !text-[10px] !text-[#faf9f5]/55" data-testid="spotlight-category">{active.category}</div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={active.slug}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="mt-5 flex-1 flex flex-col"
            >
              <div className="flex flex-wrap items-center gap-3">
                <h3 className="font-display text-3xl md:text-4xl tracking-[-0.02em] leading-[1.02]" data-testid="spotlight-active-name">{active.name}</h3>
                <span className={`font-mono tabular-nums text-[13px] font-semibold px-2 py-0.5 bg-[#a8c69f]/25 border border-[#a8c69f]/50 ${sampleTheme ? "sample-accent" : "text-[#a8c69f]"}`} data-testid="spotlight-active-dose">{active.dose}</span>
              </div>
              <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-[#faf9f5]/80" data-testid="spotlight-summary">{active.study.summary}</p>

              <div className="mt-auto pt-7 flex flex-wrap items-center gap-x-6 gap-y-3">
                <div className={sampleTheme ? "[&_button]:mt-0" : "[&_button]:!text-[#a8c69f] [&_button]:mt-0"}>
                  <StudyDialog ingredient={active} testid={`spotlight-study-${active.slug}`} sampleTheme={sampleTheme} mobileSafe={mobileSafe} />
                </div>
                <Link
                  to={`/ingredients/${active.slug}`}
                  className="group inline-flex items-center gap-1.5 label !text-[10px] !tracking-[0.16em] !text-[#faf9f5] hover:opacity-70 transition"
                  data-testid={`spotlight-more-${active.slug}`}
                >
                  Full breakdown
                  <ArrowUpRight size={13} strokeWidth={1.6} className="transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
            </motion.div>
          </AnimatePresence>
          </div>
        </div>

        {/* Right: nav rail */}
        <div className="lg:col-span-4 border-t lg:border-t-0 lg:border-l border-[#faf9f5]/12 p-6 md:p-8 flex flex-col justify-between gap-6">
          <ul className="space-y-1">
            {heroes.map((h, i) => (
              <li key={h.slug}>
                <button
                  type="button"
                  onClick={() => setIdx(i)}
                  className={`w-full text-left py-2 px-3 flex items-center justify-between transition ${sampleTheme || mobileSafe ? "gap-3" : ""} ${i === idx ? "bg-[#faf9f5]/10" : "hover:bg-[#faf9f5]/5"}`}
                  data-testid={`spotlight-tab-${h.slug}`}
                >
                  <span className={`text-[13px] ${sampleTheme || mobileSafe ? "min-w-0" : ""} ${i === idx ? "text-[#faf9f5]" : "text-[#faf9f5]/60"}`} data-testid={`spotlight-tab-name-${h.slug}`}>{h.name.split("(")[0].trim()}</span>
                  <span className={`font-mono tabular-nums text-[10px] shrink-0 ${sampleTheme ? "sample-accent" : "text-[#a8c69f]"}`} data-testid={`spotlight-tab-dose-${h.slug}`}>{h.dose}</span>
                </button>
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => go(idx - 1)} className="w-9 h-9 border border-[#faf9f5]/20 flex items-center justify-center hover:bg-[#faf9f5]/10 transition" aria-label="Previous" data-testid="spotlight-prev">
              <ChevronLeft size={16} />
            </button>
            <button type="button" onClick={() => go(idx + 1)} className="w-9 h-9 border border-[#faf9f5]/20 flex items-center justify-center hover:bg-[#faf9f5]/10 transition" aria-label="Next" data-testid="spotlight-next">
              <ChevronRight size={16} />
            </button>
            <div className="flex-1 h-px bg-[#faf9f5]/12 ml-2" />
          </div>
        </div>
      </div>
    </div>
  );
}
